<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-customer-overview-page" element-loading-text="Loading...">
    <div class="customer-overview-title-small">{{$t(currentPageInfo.subtitle)}}</div>
    <div class="button-flex">
      <div class="customer-overview-title">{{$t(currentPageInfo.title)}}</div>
      <wy-page-tooltip class="tooltip" />
    </div>
    
    <div ref="panelsContainer" class="components-container"  v-show="!loading" >
      <keep-alive v-for="(panel, index) in activePanels" :key="index">
        <component :ref="panel.key" :id="panel.key" class="panel-component-section" :is="panel.component" :controller="module" :panelTitle="panel.name" v-bind="panel.properties"></component>
      </keep-alive>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import WyCustomerOverviewPanelCustomerFeedback from "./panel-templates/wy-customer-overview-panel-customer-feedback";
import WyCustomerOverviewPanelNewAndReturningCustomers from "./panel-templates/wy-customer-overview-panel-new-and-returning-customers";
import WyCustomerOverviewPanelUsagePerCustomer from "./panel-templates/wy-customer-overview-panel-usage-per-customer";
import WyCustomerOverviewPanelLiveAgentHandling from "./panel-templates/wy-customer-overview-panel-live-agent-handling.vue";
import WyCustomerOverviewPanelConversationsByDevices from "./panel-templates/wy-customer-overview-panel-conversations-by-devices.vue"
import WyCustomerOverviewPanelConversationsByChannels from "./panel-templates/wy-customer-overview-panel-conversations-by-channels.vue";
import WyCustomerOverviewPanelCustomerLocation from "./panel-templates/wy-customer-overview-panel-customer-location.vue";
import WyCustomerOverviewJourneyAndResponseFeedback from "./panel-templates/wy-customer-overview-panel-journey-and-response-feedback";
import WyPageTooltip from '../widgets/wy-page-tooltip.vue';
import Sortable from 'sortablejs';

export default Vue.extend({
  name: 'wy-customer-overview-page',
  components: { 
    "wy-customer-overview-panel-customer-feedback": WyCustomerOverviewPanelCustomerFeedback,
    "wy-customer-overview-panel-new-and-returning-customers": WyCustomerOverviewPanelNewAndReturningCustomers,
    "wy-customer-overview-panel-usage-per-customer": WyCustomerOverviewPanelUsagePerCustomer,
    "wy-customer-overview-panel-live-agent-handling": WyCustomerOverviewPanelLiveAgentHandling,
    "wy-customer-overview-panel-conversations-by-devices": WyCustomerOverviewPanelConversationsByDevices,
    "wy-customer-overview-panel-conversations-by-channels": WyCustomerOverviewPanelConversationsByChannels,
    "wy-customer-overview-panel-customer-location": WyCustomerOverviewPanelCustomerLocation,
    "wy-customer-overview-panel-journey-and-response-feedback": WyCustomerOverviewJourneyAndResponseFeedback,
    "wy-page-tooltip": WyPageTooltip,
  },
  
  data() {
    return {
      module: this,
      initDate: null,
      pagePanels: [ 
        "wy-customer-overview-panel-customer-feedback",
        "wy-customer-overview-panel-journey-and-response-feedback",
        "wy-customer-overview-panel-new-and-returning-customers",
        "wy-customer-overview-panel-usage-per-customer",
        "wy-customer-overview-panel-live-agent-handling",
        "wy-customer-overview-panel-conversations-by-devices",
        "wy-customer-overview-panel-conversations-by-channels",
        "wy-customer-overview-panel-customer-location"
      ],
      loading: false,
      rawData: {},
      overviewData: {},
      selection: 'all',
    }
  },

  watch: {
  },

  computed: {
    ...mapGetters(['dataModel', 'isLoggedIn', 'config', 'companyConfig', 'currentPagePanels', 'currentPageName', 'currentPageInfo']),

    activePanels() {
      const pagePanelComponents = [];
      for(const pagePanel of this.pagePanels) {
        const panel = this.config.appPanels.find(el => el.key == pagePanel);
        if(panel && panel.enabled) {          
          pagePanelComponents.push({
            key: panel.key,
            name: panel.name,
            properties: panel.properties,
            component: panel.key
          })
        }
      }
      return pagePanelComponents;
		}
  },

  methods: {
    ...mapActions(['prepareCustomerBehaviourViewByData', 'getPagePanels', 'updatePagePanels']),

    handlePerformanceSelection(selection) {
      if(this.selection != selection){
        this.selection = selection;
        this.handleFilterChange();
      }
    },

    async changeDateRange(dateRange, rawData, templateData, view) {
      view.pannelData = await this.prepareCustomerBehaviourViewByData({viewBy: dateRange, rawData: rawData, templateData: templateData, companyConfig: this.companyConfig})
      if(view.pannelData.data) {
        view.sectionData = view.pannelData.data[view.dataIndex];
        view.renderLegendData();
        view.$refs.tabSection.$refs.tabBottomSection.renderLegends();
        this.refreshOverviewPanel(view, view.sectionData);
      }
    },

    async filterNumberRangeChanged(dateRange, rawData, templateData, view) {
      view.pannelData = await this.prepareCustomerBehaviourViewByData({viewBy: dateRange, rawData: rawData, templateData: templateData, companyConfig: this.companyConfig})
      view.sectionData = view.pannelData.data[view.dataIndex];
      this.refreshOverviewPanel(view, view.sectionData);
    },

    handleFilterChange() {
      this.$children.map(component => {
        if(component.handleFilterChange) {
          component.handleFilterChange();
        }
      })
    },

    refreshOverviewPanel(view, data) {
      if(view) {
        //prepare view by data
        view.pannelData.data.forEach((sectionData)=>{
          if(sectionData.key == view.sectionData.key) {
            sectionData.selected = true
          }
        })
        if(data.multiChart) {
          if(view.$refs.tabSection.$refs.lineChart || view.$refs.tabSection.$refs.pieChart) {
            // if(data.lineChartData) view.$refs.tabSection.$refs.lineChart.buildChart();
            if(data.pieChartData) view.$refs.tabSection.$refs.pieChart.buildChart();
            if(data.columnChartData) view.$refs.tabSection.$refs.comboChart.buildChart();
          }
        }
        else if(view.$refs.tabSection.$refs.lineChart || view.$refs.tabSection.$refs.pieChart) {
          if(data.lineChartData) view.$refs.tabSection.$refs.lineChart.buildChart();
          if(data.pieChartData) view.$refs.tabSection.$refs.pieChart.buildChart();
          if(data.columnChartData) view.$refs.tabSection.$refs.columnChart.buildChart();
        }
      }
    },
    
    setDefaultDateRange() {
      if(this.dataModel.dateRangeInDays < 7){
        return  'day';
      } else if(this.dataModel.dateRangeInDays < 30){
        // return 'week';
        return  'day';
      } else if(this.dataModel.dateRangeInDays < 365){
        return 'week';
        // return 'month'
      } else{
        return 'week';
        // return 'year'
      }
    }
  },

  mounted() {
    this.$root.registerTooltips(this.$el);

    Sortable.create(this.$refs.panelsContainer, { 
      handle: '.move-info',
      onUpdate: (evt) => {
        let sortedPanels = [];
        for(const el of evt.to.childNodes) {
          sortedPanels.push(el.id);
        }
        this.updatePagePanels(sortedPanels);
      }
    });
  },

  async created() {
    this.$router.currPageComponent = this;
    this.initDate = new Date();
    // listen events from other components
    this.$root.registerPageEvent(this.$route.name, (event) => {
      if(this[event.name]) this[event.name]();
    });

    this.pagePanels = this.currentPagePanels(this.pagePanels);
  },

  destroyed() {
    this.$root.unregisterTooltips(this.$el);
    if(this.isLoggedIn) {
      this.$store.dispatch('amplitudeLogWithFilters', {name: 'customer_page_time_spent', data: {time_spent: WyUtils.getTimeDiff(this.initDate)}});
    }
  }
});
</script>

<style lang="scss">
.wy-customer-overview-panel-conversations-by-devices.panel-component-section.panel-component-section-force.full-size {
  overflow: hidden;
}
.overview-page-button-poppers {
  font-family: Helvetica Neue, Helvetica;
  font-size: 14.22px;
  font-weight: 400;
}

.wy-customer-overview-page {
  // min-width: 1123px;
  // min-height: calc(100% - 20px);
  min-height: calc(100% - 30px);
  padding-right: 0px;
  font-weight: 500;
  font-size: 16px;
  padding-top: 0px;
  overflow-x: hidden;
  overflow-y: hidden;

  .header-icon-info{
    display: inline-block;
    background: url('../../assets/icons/icon_info_performance_overview.svg') no-repeat;
    width: 20px;
    height: 17px;
    position: relative;
    top: 0px !important;
    margin-left: 0.25vw;
  }
  .filter-button {
    border-radius: 20px;
    padding: 10px 20px;
    color: var(--site-font-secondary-color);
    font-size: 16px;
    font-weight: 500;
    font-family: Helvetica Neue, Helvetica;
    background-color: #FFF;
    margin-right: 0.5vw;
    border: 1px solid #DCDFE6;
  }
  .filter-button.selected {
    background: #0C2837 !important;
    border-color: #DCDFE6 !important;
    color: #FCFEFF !important;
  }

  .filter-button:focus, .filter-button:hover {
    background: #F4F6F8;
    border-color: #CAD2DA;
    color: #0C2837;
  }
  .button-flex{
    display: flex;
    align-items: center;
  }
  .tooltip {
    padding-left: 8px;
    padding-bottom: 8px;
  }
  .customer-overview-title-small{
    color: var(--site-font-secondary-color);
    font-size: 15px;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-weight: 400;
  }
  .customer-overview-title{
    margin-bottom: 0.8vw;
    color: var(--site-font-secondary-color);
    font-size: 25px;
    font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-weight: 600;
  }

// global panel container layout

  // .panel-component-section-force:nth-child(even) {
  //   width: calc(100% - 150px);
  // }

  .panel-component-section {
    min-height: calc(100% - 20px);
    padding-right: 0px;
    margin-bottom: -26px;
    background: #FFF;
    border: 1px solid #CAD2DA;
    width: 100%;
    height: auto;
    border-radius: 5px;
    margin-top: 20px;
    // margin-right: 0px;
    // margin-right: 20px;
    margin-right: -41px;

    .el-loading-mask {
      // height: 580px !important;
      height: calc(100% - 80px) !important;
      top: 66px;
    }
    .full-size .el-loading-mask {
      top: 85px;
      height: calc(100% - 64px) !important;
    }
    .bottom-right-flex {
      .chart-header {
        display: flex;
        .compare-group-by {
          margin-right: 20px;
          margin-left: auto;
        }
      }
    }
  }
  .components-container{
    width: 99.5%;
    margin-top: -6px;
    margin-bottom: 50px;
    display: grid;
    column-gap: 25px;
    row-gap: 25px;
    grid-template-columns: 1fr 1fr;
    display: grid;
    grid-gap: 1rem;
    // grid-template-columns: calc(50% - 25px);
    grid-template-columns: 50%;
    grid-template-areas: 'item item';
    

    .wy-customer-overview-row > div:last-child {
      margin-right: 0px;
    }
  }
  
}

@media screen  and (min-width: 2000px) {
  .wy-customer-overview-page {
    // .panel-component-section-force:nth-child(even) {
    //   width: calc(100% - 150px);
    // }
    .components-container .panel-component-section {
      margin-bottom: -28px;
      .panel-title {
        font-size: 0.9vw;
      }
      span.panel-type {
        padding: 1vw;
        font-size: 0.9vw;
      }
      .value-previous-days{
        //padding-left: 0.8vw; OC-684
        line-height: 1.6vw;
        font-size: 0.9vw;
        height: 1.6vw;
        padding: 0.2vw 0.9vw 0.2vw 1.4vw
      }
      .arrow-up {
        background-position-y: 0.58vw;
        background-position-x: 12.6px;
        background-size: 0.68vw;
      }
      .arrow-down {
        background-position-y: 0.58vw;
        background-position-x: 12.6px;
        background-size: 0.68vw;
      }
      .value-start-period{
        line-height: 1.6vw;
        font-size: 0.9vw;
        height: 1.6vw;
        padding: 0.2vw 0.9vw 0.2vw 1.4vw
      }
      .change-over-time-texts{
        font-size: 0.66vw;
      }
      .bottom-flex-title{
        font-size: 16px;
      }
      .panel-top-flex{
        // border-right: 1px #CAD2DA solid;
        // width: 337px;
        width: 100%;
        height: 120px
      }
      .panel-top--title {
        font-size: 14.5px;
      }
      .panel-body-flex{
        width: 100%;
        // height: 300px;
        display: flex;
        // flex-direction: column;
        .div-pie-section {
          width: 40%;
        }
        .div-column-section {
          width: 60%;
        }
      }
      .panel-bottom-flex {
        .chart-legend {
          width: 100%;
          height: 50px;
        }
      }
      .panel-component-section {
        min-height: calc(100% - 20px);
        padding-right: 0px;
        // margin-bottom: 10px;
        border: 1px solid #CAD2DA;
        width: 50%;
        height: 100%;
        border-radius: 5px;
        margin-top: 20px;
        margin-right: 20px;
        .panel-top-flex{
          // border-right: 1px #CAD2DA solid;
          // width: 337px;
          width: 100%;
          height: 100px
        }
      }
      .VueCarousel-slide:first-child {
        .tab-section {
          padding-left: 14px;
          width: calc(100% - 14px);
          .stats, .stats-title {  
            padding-left: 2px;
          }
        }
      }
      
      .wy-panel-top-section {
        .overview-table {
          padding-left: 0px;
          td {
            .tab-section {
              border-bottom: 0.2vw transparent solid;
              text-align: left;
              // padding-left: 25px;
              // padding-left: 16px;
              // width: 9.7vw;

              .stats {
                font-size: 1.6vw;
              }
              .stats-title {
                font-size: 0.817vw;
                margin-bottom: 0.3vw;
              }
            }
            .tab-section.selected {
              border-bottom: 0.2vw #000 solid;
            }
          }
        }
      }
    }
  }
}
@media screen and (min-width: 1400px) and (max-width: 1600px) {
  .change-over-time-texts{
    font-size: 12px !important;
  }
}
@media screen and (min-width: 1085px) and (max-width: 1400px) {
  .components-container{
    grid-template-columns: 100% !important;
    grid-template-areas: unset !important;
  }
  .wy-customer-overview-page {
    .customer-overview-title-small{
      font-size: 1.3vw;
    }
    .customer-overview-title{
      // margin-bottom: 0.8vw;
      font-size: 2.3vw;
    }
    .filter-button{
      font-size: 1.3vw;
    }
    // .components-container{
    //   grid-template-columns: unset!important;
    // }
    .components-container .panel-component-section{
      display: block;
      margin-bottom: -22px;
      .panel-title {
        font-size: 1.5vw;
      }
      span.panel-type {
        padding: 1vw;
        font-size: 1.2vw;
      }
      .panel-component-section {
        width: calc(100% - 2px);
        height: 100%;
      }

      .value-previous-days{
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 2vw;
        padding-right: 10px;
        line-height: 2.9vw;
        height: 2.8vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .value-start-period{
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 2vw;
        padding-right: 10px;
        line-height: 2.9vw;
        height: 2.8vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .arrow-up {
        background-position-y: 0.8vw;
        background-position-x: 8px;
        background-size: 1.08vw;
      }
      .arrow-down {
        background-position-y: 0.8vw;
        background-position-x: 8px;
        background-size: 1.08vw;
      }
      .VueCarousel-slide:first-child {
        .tab-section {
          padding-left: 14px !important;
          width: calc(100% - 14px) !important;
        }
      }
      .overview-table td .tab-section {
        width: 100% !important;
      }
      .panel-top--title {
        font-size: 14.5px;
      }


      .change-over-time-texts{
        width: 55%;
        height: 36px;
        font-size: 1.2vw;
        font-family: Helvetica Neue, Helvetica;
        color: var(--site-font-tertiary-color);
        font-weight: normal;
        padding: 0 10px 0 10px;
      }
      .bottom-flex-title{
        padding: 15px;
        font-size: 1.2vw;
        font-weight: medium;
        color: var(--site-font-secondary-color);
      }
      .wy-panel-top-section .overview-table td .tab-section.selected {
        border-bottom: 0.3vw #0C283F solid !important;
      }
      .wy-panel-top-section {
        .overview-table {
          padding-left: 0px;
          border-bottom: 1px #CAD2DA solid;
          width: 100%;
          td {
            .tab-section {
              border-bottom: 0.2vw transparent solid;
              text-align: left;
              padding: 18px 14px;
              padding-right: 0px !important;
              cursor: pointer;

              &:hover{
                background: #F4F6F8;
              }

              .stats {
                .section-value {
                  font-size: 2.4vw;
                  color: var(--site-font-secondary-color);
                }
                margin-right: 5px;
              }
              .stats-title {
                font-size: 1.4vw;
                color: var(--site-font-tertiary-color);
              }
              svg.ct-chart-line {
                width: 100% !important;
              }
            }
          }
        }
      }

      .panel-bottom-flex {
        height: 105px;
      }
    }
  }
}
@media screen and (max-width: 1085px) {

  .components-container{
    grid-template-columns: 100% !important;
    grid-template-areas: unset !important;
    min-width: 500px;
  }
  .wy-customer-overview-page {
    .customer-overview-title-small{
      font-size: 1.3vw;
    }
    .customer-overview-title{
      // margin-bottom: 0.8vw;
      font-size: 2.3vw;
    }
    .filter-button{
      font-size: 1.3vw;
    }
    // .components-container{
    //   grid-template-columns: unset!important;
    // }
    .components-container .panel-component-section{
      display: block;
      margin-bottom: -22px;

      .panel-title {
        font-size: 1.5vw;
      }
      span.panel-type {
        padding: 1vw;
        font-size: 1.2vw;
      }
      .panel-component-section {
        width: calc(100% - 2px);
        height: 100%;
      }

      .value-previous-days{
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 1.9vw;
        padding-right: 9px;
        line-height: 2.9vw;
        height: 2.9vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .value-start-period{
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 1.9vw;
        padding-right: 9px;
        line-height: 2.9vw;
        height: 2.9vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .arrow-up {
        background-position-y: 0.84vw;
        background-position-x: 6px;
        background-size: 1.07vw;
      }
      .arrow-down {
        background-position-y: 0.84vw;
        background-position-x: 6px;
        background-size: 1.07vw;
      }
      .VueCarousel-slide:first-child {
        .tab-section {
          padding-left: 14px !important;
          width: calc(100% - 14px)!important;
        }
      }
      .overview-table td .tab-section {
        width: 100% !important;
      }
      .panel-top--title {
        font-size: 14.5px;
      }


      .change-over-time-texts{
        width: 55%;
        height: 36px;
        font-size: 1.2vw;
        font-family: Helvetica Neue, Helvetica;
        color: var(--site-font-tertiary-color);
        font-weight: normal;
        padding: 0 10px 0 10px;
      }
      .bottom-flex-title{
        padding: 15px;
        font-size: 1.2vw;
        font-weight: medium;
        color: var(--site-font-secondary-color);
      }
      .wy-panel-top-section .overview-table td .tab-section.selected {
        border-bottom: 0.3vw #0C283F solid !important;
      }
      .wy-panel-top-section {
        .overview-table {
          padding-left: 0px;
          border-bottom: 1px #CAD2DA solid;
          width: 100%;
          td {
            .tab-section {
              border-bottom: 0.2vw transparent solid;
              text-align: left;
              padding: 18px 14px;
              padding-right: 0px !important;
              cursor: pointer;

              &:hover{
                background: #F4F6F8;
              }

              .stats {
                .section-value {
                  font-size: 2.4vw;
                  color: var(--site-font-secondary-color);
                }
                margin-right: 5px;
              }
              .stats-title {
                font-size: 1.4vw;
                color: var(--site-font-tertiary-color);
              }
              svg.ct-chart-line {
                width: 100% !important;
              }
            }
          }
        }
      }

      .panel-bottom-flex {
        height: 105px;
      }
    }
  }
}
</style>